import React, { useContext, useMemo } from "react";

import RatingStars from "../../Common/RatingStars";
import { getRatingAccentColor } from "../../Common/ratingPalette";
import { ReservationCardUI, StatusUI } from "../../UI";
import ProfileListMeta from "./ProfileListMeta";
import { StudentInfoContext } from "./StudentInfoContext";
import { useProfileAlert } from "./useProfileAlert";

function formatRatingNumber(value) {
   const number = Number(value);
   if (!Number.isFinite(number)) return "—";
   return number % 1 === 0 ? String(number) : number.toFixed(1);
}

function getInstructorName(rating) {
   const person = rating?.instructor;
   if (!person) return "Instructor necunoscut";
   const first = String(person.firstName || "").trim();
   const last = String(person.lastName || "").trim();
   const full = `${first} ${last}`.trim();
   return full || person.email || "Instructor necunoscut";
}

function getReviewDateLabel(rating) {
   const raw =
      rating?.reservation?.startTime ||
      rating?.reservation?.date ||
      rating?.createdAt ||
      rating?.updatedAt;
   if (!raw) return "";

   const date = new Date(raw);
   if (Number.isNaN(date.getTime())) return "";

   return date.toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
   });
}

function getReviewSortMs(rating) {
   const raw =
      rating?.createdAt ||
      rating?.updatedAt ||
      rating?.reservation?.startTime ||
      0;
   const ms = new Date(raw).getTime();
   return Number.isFinite(ms) ? ms : 0;
}

function getReviewId(rating) {
   return (
      rating?.id ??
      `${rating?.reservationId || "r"}-${rating?.instructorId || "i"}-${
         rating?.createdAt || "c"
      }`
   );
}

export default function StudentReviewsTab() {
   const {
      reviews,
      reviewsLoading,
      reviewsError,
      reviewsAscending,
      setReviewsAscending,
   } = useContext(StudentInfoContext);
   useProfileAlert(reviewsError);

   const sortedReviews = useMemo(() => {
      const clone = Array.isArray(reviews) ? [...reviews] : [];
      clone.sort((a, b) => {
         const diff = getReviewSortMs(a) - getReviewSortMs(b);
         return reviewsAscending ? diff : -diff;
      });
      return clone;
   }, [reviews, reviewsAscending]);

   return (
      <div className="studentsProfileUI__reviews">
         <ProfileListMeta
            count={sortedReviews.length}
            one="recenzie"
            many="recenzii"
            ascending={reviewsAscending}
            onToggleSort={() => setReviewsAscending((prev) => !prev)}
            sortLabels={["Sortează de la cele mai vechi", "Sortează de la cele mai noi"]}
         />

         {reviewsLoading ? (
            <StatusUI tone="loading">Se încarcă recenziile…</StatusUI>
         ) : reviewsError ? null : !sortedReviews.length ? (
            <StatusUI tone="empty">Acest elev nu a lăsat nicio recenzie.</StatusUI>
         ) : (
            // recenziile, casete lipite ca rezervările și examenele (ReservationCardUI): instructorul sus,
            // nota în colț (pastila de sticlă), dedesubt data, stelele și comentariul
            <ReservationCardUI.List>
               {sortedReviews.map((rating) => {
                  const comment = String(rating?.comment || "").trim();
                  const accent = getRatingAccentColor(rating?.rating) || undefined;
                  return (
                     <ReservationCardUI
                        key={getReviewId(rating)}
                        date={getInstructorName(rating)}
                        number={formatRatingNumber(rating?.rating)}
                        lines={[
                           getReviewDateLabel(rating) || "—",
                           <RatingStars value={rating?.rating} size="sm" accentColor={accent} />,
                           <span className={"studentsProfileUI__reviewComment" + (comment ? "" : " is-empty")}>{comment || "Fără comentariu."}</span>,
                        ]}
                     />
                  );
               })}
            </ReservationCardUI.List>
         )}
      </div>
   );
}
